import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  IoCashOutline, IoCubeOutline, IoTrendingUpOutline, IoTrendingDownOutline,
  IoCarOutline, IoAlertCircleOutline, IoArrowForwardOutline,
  IoShieldCheckmarkOutline, IoBarcodeOutline, IoTimeOutline,
  IoStorefrontOutline, IoCheckmarkCircle, IoStatsChartOutline, IoReceiptOutline,
  IoReturnDownBackOutline,
} from 'react-icons/io5';
import { api, type DashboardKpis } from '../api';
import { useAuth } from '../auth';
import { ROLE_LABELS, type Role } from '../permissions';
import { AdminAiPanel, ManagerAiPanel, WarehouseAiPanel, ViewerAiPanel, EmbeddedChat } from './dashboard/AiWidgets';

const RUB = (v: number) => Math.round(v).toLocaleString('ru-RU') + ' ₽';

export function DashboardPage() {
  const { user } = useAuth();
  const role = user?.role as Role | undefined;
  if (role === 'warehouse') return <WarehouseDashboard />;
  return <FullDashboard restricted={role === 'viewer'} />;
}

// ─────────────────────────────────────────────────────────
// Полный дашборд (admin, manager, viewer)
// ─────────────────────────────────────────────────────────
function FullDashboard({ restricted }: { restricted: boolean }) {
  const { user } = useAuth();
  const role = user?.role as Role | undefined;
  const kpis = useQuery({ queryKey: ['dashboard-kpis'], queryFn: api.dashboardKpis, refetchInterval: 60_000 });
  const aiW = useQuery({ queryKey: ['dashboard-ai-widgets'], queryFn: api.dashboardAiWidgets, refetchInterval: 5 * 60_000 });

  if (kpis.isLoading) return <div className="card">Загрузка KPI…</div>;
  if (kpis.error) return <div className="card" style={{ color: 'var(--red)' }}>{(kpis.error as Error).message}</div>;
  if (!kpis.data) return null;

  const k = kpis.data;
  const todayProfit = k.today.revenue + k.today.commission;
  const yesterdayProfit = k.yesterday.revenue + k.yesterday.commission;
  const monthProfit = k.month.revenue + k.month.commission;
  const today = new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Дашборд</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            {capitalize(today)} · {user?.name ?? user?.email} ({ROLE_LABELS[user?.role as Role]})
            {restricted && ' · режим просмотра'}
          </div>
        </div>
      </div>

      <AlertsRow alerts={k.alerts} />

      {/* AI-виджеты сверху — Admin получает executive briefing + health */}
      {aiW.data && role === 'admin' && <AdminAiPanel w={aiW.data} />}

      <SectionTitle>Ключевые показатели</SectionTitle>
      <div className="dashboard-kpi-grid" style={{ marginBottom: 24 }}>
        <KpiCard icon={<IoCashOutline />} label="Выручка сегодня" value={RUB(k.today.revenue)}
          trend={trend(k.today.revenue, k.yesterday.revenue)} trendLabel="к вчера"
          sub={`${k.today.orders} зак · ср.чек ${RUB(k.today.orders > 0 ? k.today.revenue / k.today.orders : 0)}`}
          accent />
        <KpiCard icon={<IoTrendingUpOutline />} label="Прибыль сегодня" value={RUB(todayProfit)}
          trend={trend(todayProfit, yesterdayProfit)} trendLabel="к вчера"
          sub={`Комиссия ${commissionRate(k.today.revenue, k.today.commission)}%`} />
        <KpiCard icon={<IoStatsChartOutline />} label="Выручка за 7 дней" value={RUB(k.week.revenue)}
          trend={trend(k.week.revenue, k.prevWeek.revenue)} trendLabel="к прошл. неделе"
          sub={`${k.week.orders} заказов`} />
        <KpiCard icon={<IoReceiptOutline />} label="Выплата за 30 дней" value={RUB(k.month.payout)}
          trend={trend(k.month.revenue, k.prevMonth.revenue)} trendLabel="к пред. месяцу"
          sub={`Прибыль ${RUB(monthProfit)}`} />
      </div>

      <div className="dashboard-row">
        {k.sparkline.length > 0 && (
          <div className="card">
            <div style={{ padding: '20px 20px 12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h3 style={{ margin: 0 }}>Выручка за 30 дней</h3>
                <span className="muted" style={{ fontSize: 12 }}>
                  Итого: <strong style={{ color: 'var(--text)' }}>{RUB(k.month.revenue)}</strong>
                </span>
              </div>
            </div>
            <Sparkline data={k.sparkline} />
          </div>
        )}

        {k.topProducts.length > 0 && (
          <div className="card">
            <div style={{ padding: '20px 20px 0' }}>
              <h3 style={{ margin: 0 }}>Топ за неделю</h3>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>По выручке</div>
            </div>
            <div style={{ padding: '12px 20px 16px' }}>
              {k.topProducts.map((p, i) => (
                <div key={p.sku} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < k.topProducts.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div className="top-rank">{i + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div className="muted" style={{ fontSize: 11 }}>{p.orders_count} прод. · SKU {p.sku}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', whiteSpace: 'nowrap' }}>{RUB(p.revenue)}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Manager: action items + pricing + drafts ниже KPI */}
      {aiW.data && role === 'manager' && (
        <>
          <SectionTitle>Действия и AI-инсайты</SectionTitle>
          <ManagerAiPanel w={aiW.data} />
        </>
      )}

      {/* Viewer: дайджест + market trend; embedded chat — плавающая кнопка */}
      {aiW.data && role === 'viewer' && (
        <>
          <SectionTitle>AI-аналитика</SectionTitle>
          <ViewerAiPanel w={aiW.data} />
        </>
      )}

      {/* Admin тоже видит manager-блок (если есть данные) */}
      {aiW.data && role === 'admin' && (
        <>
          <SectionTitle>Что сделать сегодня</SectionTitle>
          <ManagerAiPanel w={aiW.data} />
        </>
      )}

      {role === 'viewer' && <EmbeddedChat />}

      <SectionTitle>Быстрые переходы</SectionTitle>
      <div className="quick-links">
        <QuickLink to="/finance" title="Финансы" subtitle="Транзакции и комиссии" icon={<IoCashOutline />} />
        <QuickLink to="/shipments" title="Отгрузки" subtitle="Сегодняшняя сборка" icon={<IoCarOutline />} />
        <QuickLink to="/products" title="Товары" subtitle="Каталог и цены" icon={<IoCubeOutline />} />
        <QuickLink to="/returns" title="Возвраты" subtitle="Причины и аналитика" icon={<IoAlertCircleOutline />} />
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Дашборд склада
// ─────────────────────────────────────────────────────────
function WarehouseDashboard() {
  const { user } = useAuth();
  const kpis = useQuery({ queryKey: ['dashboard-kpis'], queryFn: api.dashboardKpis, refetchInterval: 30_000 });
  const returns = useQuery({ queryKey: ['returns', 'warehouse-dashboard'], queryFn: () => api.returnsList({ limit: 200 }), refetchInterval: 60_000 });
  const aiW = useQuery({ queryKey: ['dashboard-ai-widgets'], queryFn: api.dashboardAiWidgets, refetchInterval: 5 * 60_000 });

  if (kpis.isLoading) return <div className="card">Загрузка…</div>;
  if (!kpis.data) return null;
  const a = kpis.data.alerts;
  const today = new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' });

  // Группировка возвратов по статусу — важно для склада
  const returnsByStatus = (returns.data?.items ?? []).reduce<Record<string, number>>((acc, r) => {
    const key = r.status || 'unknown';
    acc[key] = (acc[key] ?? 0) + 1;
    return acc;
  }, {});
  const toPickup = (returnsByStatus['ReadyForDelivery'] ?? 0)
    + (returnsByStatus['WaitingForSeller'] ?? 0)
    + (returnsByStatus['Returning'] ?? 0); // ожидают забора в ПВЗ
  const inTransit = (returnsByStatus['MovingToSeller'] ?? 0)
    + (returnsByStatus['DriverPickup'] ?? 0); // в пути к нам
  const received = returnsByStatus['ReceivedBySeller'] ?? 0;

  return (
    <>
      <div className="page-header">
        <div>
          <h2>Сборка и отгрузка</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            {capitalize(today)} · {user?.name ?? user?.email}
          </div>
        </div>
      </div>

      <SectionTitle>Отгрузки</SectionTitle>
      <div className="warehouse-tile-grid">
        <Link to="/shipments" className={`warehouse-tile ${a.totalAwaitingPackaging > 0 ? 'urgent' : 'ok'}`}>
          <div className="warehouse-tile-icon"><IoBarcodeOutline /></div>
          <div className="warehouse-tile-num">{a.totalAwaitingPackaging}</div>
          <div className="warehouse-tile-label">К сборке</div>
          <div className="warehouse-tile-action">Открыть <IoArrowForwardOutline /></div>
        </Link>

        <Link to="/shipments" className={`warehouse-tile ${a.urgentShipments > 0 ? 'urgent' : ''}`}>
          <div className="warehouse-tile-icon"><IoTimeOutline /></div>
          <div className="warehouse-tile-num">{a.urgentShipments}</div>
          <div className="warehouse-tile-label">Срочно (&lt;4ч)</div>
          <div className="warehouse-tile-action">Открыть <IoArrowForwardOutline /></div>
        </Link>

        <Link to="/shipments/picking-list" className="warehouse-tile">
          <div className="warehouse-tile-icon"><IoStorefrontOutline /></div>
          <div className="warehouse-tile-num"><IoArrowForwardOutline /></div>
          <div className="warehouse-tile-label">Список сборки</div>
          <div className="warehouse-tile-action">Напечатать <IoArrowForwardOutline /></div>
        </Link>

        <Link to="/shipments" className="warehouse-tile success">
          <div className="warehouse-tile-icon"><IoCheckmarkCircle /></div>
          <div className="warehouse-tile-num">FBS</div>
          <div className="warehouse-tile-label">Готовы к отгрузке</div>
          <div className="warehouse-tile-action">Этикетки <IoArrowForwardOutline /></div>
        </Link>
      </div>

      {/* Возвраты — для склада: что забрать с ПВЗ Ozon, что в пути */}
      <div>
        <SectionTitle>Возвраты</SectionTitle>
        <div className="warehouse-tile-grid">
          <Link to="/returns?status=ReadyForDelivery" className={`warehouse-tile ${toPickup > 0 ? 'urgent' : ''}`}>
            <div className="warehouse-tile-icon"><IoStorefrontOutline /></div>
            <div className="warehouse-tile-num">{toPickup}</div>
            <div className="warehouse-tile-label">Забрать из ПВЗ Ozon</div>
            <div className="warehouse-tile-action">К списку <IoArrowForwardOutline /></div>
          </Link>

          <Link to="/returns?status=MovingToSeller" className="warehouse-tile">
            <div className="warehouse-tile-icon"><IoCarOutline /></div>
            <div className="warehouse-tile-num">{inTransit}</div>
            <div className="warehouse-tile-label">В пути на склад</div>
            <div className="warehouse-tile-action">К списку <IoArrowForwardOutline /></div>
          </Link>

          <Link to="/returns?status=ReceivedBySeller" className="warehouse-tile success">
            <div className="warehouse-tile-icon"><IoReturnDownBackOutline /></div>
            <div className="warehouse-tile-num">{received}</div>
            <div className="warehouse-tile-label">Принято на склад</div>
            <div className="warehouse-tile-action">К списку <IoArrowForwardOutline /></div>
          </Link>

          <Link to="/returns" className="warehouse-tile">
            <div className="warehouse-tile-icon"><IoReturnDownBackOutline /></div>
            <div className="warehouse-tile-num">{returns.data?.total ?? 0}</div>
            <div className="warehouse-tile-label">Всего возвратов</div>
            <div className="warehouse-tile-action">Все <IoArrowForwardOutline /></div>
          </Link>
        </div>
      </div>

      {/* Warehouse AI: stockouts + incoming returns + image flags */}
      {aiW.data && (
        <>
          <SectionTitle>AI-предупреждения</SectionTitle>
          <WarehouseAiPanel w={aiW.data} />
        </>
      )}

      <SectionTitle>Памятка</SectionTitle>
      <div className="card" style={{ padding: 20 }}>
        <h3 style={{ margin: 0, marginBottom: 12 }}>Порядок работы</h3>
        <ol style={{ paddingLeft: 20, fontSize: 14, lineHeight: 1.8, margin: 0 }}>
          <li><strong>Распечатать «Список сборки»</strong> — взять со стеллажа все товары на сегодня</li>
          <li>Зайти в <strong>каждое отправление</strong>, отсканировать маркировку «Честный знак»</li>
          <li>После сборки всех — <strong>«Все этикетки PDF»</strong>, наклеить на упаковки</li>
          <li>Когда курьер приедет — <strong>«Запросить документ»</strong> и распечатать ТН/акт</li>
        </ol>
        <Link to="/shipments" className="btn primary" style={{ marginTop: 16 }}>
          К отгрузкам <IoArrowForwardOutline />
        </Link>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────
// Shared
// ─────────────────────────────────────────────────────────
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="section-title">
      <span>{children}</span>
      <span className="section-title-line" />
    </div>
  );
}

function KpiCard({ icon, label, value, sub, trend, trendLabel, accent }: {
  icon: React.ReactNode; label: string; value: string; sub?: string;
  trend?: { pct: number; positive: boolean } | null; trendLabel?: string; accent?: boolean;
}) {
  return (
    <div className="kpi-card">
      <div className="kpi-icon">{icon}</div>
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value${accent ? ' accent' : ''}`}>{value}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
        {trend && Number.isFinite(trend.pct) && Math.abs(trend.pct) >= 0.1 && (
          <span className={`trend-pill ${trend.positive ? 'up' : 'down'}`}>
            {trend.positive ? <IoTrendingUpOutline /> : <IoTrendingDownOutline />}
            {Math.abs(trend.pct).toFixed(0)}%
          </span>
        )}
        {sub && <div className="kpi-sub">{trendLabel && trend ? trendLabel + ' · ' : ''}{sub}</div>}
      </div>
    </div>
  );
}

function AlertsRow({ alerts }: { alerts: DashboardKpis['alerts'] }) {
  const items: { color: 'red' | 'yellow' | 'green'; icon: React.ReactNode; label: string; value: number; link: string }[] = [];
  if (alerts.urgentShipments > 0) items.push({ color: 'red', icon: <IoAlertCircleOutline />, label: 'Срочные отгрузки (<4ч)', value: alerts.urgentShipments, link: '/shipments' });
  if (alerts.rrpViolations > 0) items.push({ color: 'red', icon: <IoShieldCheckmarkOutline />, label: 'Нарушений РРЦ', value: alerts.rrpViolations, link: '/products?rrp=violation' });
  if (alerts.outOfStock > 0) items.push({ color: 'yellow', icon: <IoCubeOutline />, label: 'Товаров без остатков', value: alerts.outOfStock, link: '/products?stock=out' });
  if (alerts.lowStock > 0) items.push({ color: 'yellow', icon: <IoCubeOutline />, label: 'Мало остатков (<5)', value: alerts.lowStock, link: '/products' });
  if (alerts.totalAwaitingPackaging > 0) items.push({ color: 'green', icon: <IoCarOutline />, label: 'К сборке', value: alerts.totalAwaitingPackaging, link: '/shipments' });

  if (items.length === 0) {
    return (
      <div className="card" style={{ marginBottom: 20, padding: 14, background: 'var(--green-soft)', color: '#065f2e', display: 'flex', alignItems: 'center', gap: 8 }}>
        <IoCheckmarkCircle style={{ fontSize: 20 }} />
        <strong>Всё в порядке</strong> — нет срочных задач и алертов.
      </div>
    );
  }

  return (
    <div className="alerts-row" style={{ marginBottom: 24 }}>
      {items.map((a, i) => (
        <Link key={i} to={a.link} className={`alert-card alert-${a.color}`}>
          <div className="alert-card-icon">{a.icon}</div>
          <div className="alert-card-body">
            <div className="alert-card-value">{a.value}</div>
            <div className="alert-card-label">{a.label}</div>
          </div>
          <IoArrowForwardOutline className="alert-card-arrow" />
        </Link>
      ))}
    </div>
  );
}

function QuickLink({ to, title, subtitle, icon }: { to: string; title: string; subtitle: string; icon: React.ReactNode }) {
  return (
    <Link to={to} className="quick-link">
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ fontSize: 22, color: 'var(--accent)' }}>{icon}</div>
        <div>
          <div className="quick-link-title">{title}</div>
          <div className="quick-link-subtitle">{subtitle}</div>
        </div>
      </div>
      <IoArrowForwardOutline />
    </Link>
  );
}

function Sparkline({ data }: { data: { day: string; revenue: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.revenue));
  const width = 600;
  const height = 140;
  const stepX = data.length > 1 ? width / (data.length - 1) : width;

  const points = data.map((d, i) => {
    const x = i * stepX;
    const y = height - (d.revenue / max) * (height - 24) - 12;
    return [x, y, d];
  }) as [number, number, { day: string; revenue: number }][];

  const pathLine = points.map(([x, y], i) => (i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`)).join(' ');
  const pathArea = `${pathLine} L ${width} ${height} L 0 ${height} Z`;

  return (
    <div style={{ padding: '0 20px 20px' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" preserveAspectRatio="none" style={{ maxHeight: 180 }}>
        <defs>
          <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#005bff" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#005bff" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={pathArea} fill="url(#sparkGrad)" />
        <path d={pathLine} fill="none" stroke="#005bff" strokeWidth="2.5" strokeLinejoin="round" />
        {points.map(([x, y, d], i) => (
          <g key={i}>
            <circle cx={x} cy={y} r="3.5" fill="#005bff" />
            <title>{new Date(d.day).toLocaleDateString('ru-RU')}: {RUB(d.revenue)}</title>
          </g>
        ))}
      </svg>
    </div>
  );
}

function trend(current: number, previous: number): { pct: number; positive: boolean } | null {
  if (previous === 0 || !Number.isFinite(previous)) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  return { pct, positive: pct >= 0 };
}

function commissionRate(revenue: number, commission: number): string {
  if (revenue <= 0) return '0';
  return ((-commission / revenue) * 100).toFixed(1);
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
